import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify'

export const BudgetLineItemLoadingKeys = {
    getBudgetLineItems: "GET_ALL_BUDGET_LINE_ITEMS",
    getBudgetLineItemsBySourceAccount: "GET_ALL_BUDGET_LINE_ITEMS_BY_SOURCE_ACCOUNT",
    createBudgetLineItem: "CREATE_BUDGET_LINE_ITEM",
    updateLineItem: "UPDATE_BUDGET_LINE_ITEM",
    updateMonthValue: "UPDATE_MONTH_VALUE",
    updateFormulaByBudgetLineItem: "UPDATE_FORMULA_BY_BUDGET",
    deleteBudgetLineItem: "DELETE_BUDGET_LINE_ITEM",
}

const initialState = {
    loading: false,
    budgetLineItems: [],
    budgetLineItemsBySourceAccount: [],
    budgetLineItemsTotals: {},
    budgetLineItemsRollups: [],
    loadingKeys: [],
    firstLoadComplete: false,
    error: null,
}

export const getBudgetLineItemsBySourceAccount = createAsyncThunk(
    'budgetLineItems/getBudgetLineItemsBySourceAccount',
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget-line-items-by-source-account', { params: { 
                id,
            }});
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const upsertBudgetLineItemFormulaRequest = createAsyncThunk(
    'budgetLineItems/upsertBudgetLineItemFormulaRequest',
    async ({
        id,
        budgetId,
        selectedOperation,
        selectedSourceAccount,
        currentValue,
        isPercentageValue,
        isSeparateValueByMonths,
        percentageValueByMonths,
        isRemove
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/formula-line-item', { 
                id,
                budgetId,
                selectedOperation,
                selectedSourceAccount,
                currentValue,
                isPercentageValue,
                isSeparateValueByMonths,
                percentageValueByMonths,
                isRemove
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getBudgetLineItemsCards = createAsyncThunk(
    'budgetLineItems/getBudgetLineItemsCards',
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget-line-items', { params: { 
                id,
            }});
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateBudgetLineItemRequest = createAsyncThunk(
    'budgetLineItems/updateBudgetLineItemRequest',
    async ({
        selectedType, 
        code,
        name,
        selectedForecast, 
        selectedForecastValue, 
        selectedCategory, 
        budgetLineItemId, 
        selectedRollUpItems, 
        orderUpdates
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget-line-items/update', { 
                selectedType, 
                code,
                name,
                selectedForecast, 
                selectedForecastValue, 
                selectedCategory, 
                budgetLineItemId, 
                selectedRollUpItems, 
                orderUpdates 
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const upsertBudgetLineItemRequest = createAsyncThunk(
    'budgetLineItems/upsertBudgetLineItemRequest',
    async ({
        accountName, accountNumber, selectedType, selectedCategory, defaultMonthValue, budgetReportId
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget-line-items/create', { accountName, accountNumber, selectedType, selectedCategory, defaultMonthValue, budgetReportId });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const deleteBudgetLineItemRequest = createAsyncThunk(
    'budgetLineItems/deleteBudgetLineItemRequest',
    async ({
        id
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/budget-line-items/delete/${id}`);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateMonthValueRequest = createAsyncThunk(
    'budgetLineItems/updateMonthValueRequest',
    async ({
        id, monthKey, value, budgetId
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget-line-items/month-value', { id, monthKey, value, budgetId });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const budgetLineItemSlice = createSlice({
    name: 'budgetLineItems',
    initialState,
    reducers: {
        clearBudgetLineItems: (state) => {
            state.budgetLineItems = [];
            state.budgetLineItemsTotals = {};
            state.budgetLineItemsRollups = [];
            state.error = null;
        },
        clearBudgetLineItemsBySourceAccount: (state) => {
            state.budgetLineItemsBySourceAccount = [];
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getBudgetLineItemsBySourceAccount.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.getBudgetLineItemsBySourceAccount];
            })
            .addCase(getBudgetLineItemsBySourceAccount.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.budgetLineItemsBySourceAccount = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.getBudgetLineItemsBySourceAccount, state.loadingKeys);
            })
            .addCase(getBudgetLineItemsBySourceAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.budgetLineItemsBySourceAccount = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.getBudgetLineItemsBySourceAccount, state.loadingKeys);
            })
            .addCase(getBudgetLineItemsCards.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.getBudgetLineItems];
            })
            .addCase(getBudgetLineItemsCards.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.budgetLineItems = action.payload.items;
                state.budgetLineItemsTotals = action.payload.totals;
                state.budgetLineItemsRollups = action.payload.rollups;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.getBudgetLineItems, state.loadingKeys);
            })
            .addCase(getBudgetLineItemsCards.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.budgetLineItems = [];
                state.budgetLineItemsTotals = {};
                state.budgetLineItemsRollups = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.getBudgetLineItems, state.loadingKeys);
            })
            .addCase(updateBudgetLineItemRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.updateLineItem];
            })
            .addCase(updateBudgetLineItemRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateLineItem, state.loadingKeys);
                toast.success(`Budget line item updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateBudgetLineItemRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateLineItem, state.loadingKeys);
                toast.error(
                    `Failed to update budget line item. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(upsertBudgetLineItemRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.createBudgetLineItem];
            })
            .addCase(upsertBudgetLineItemRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.createBudgetLineItem, state.loadingKeys);
                toast.success(`Budget line item created successfully.`, { position: "bottom-right" })
            })
            .addCase(upsertBudgetLineItemRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.createBudgetLineItem, state.loadingKeys);
                toast.error(
                    `Failed to create budget line item. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteBudgetLineItemRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.deleteBudgetLineItem];
            })
            .addCase(deleteBudgetLineItemRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.deleteBudgetLineItem, state.loadingKeys);
                toast.success(`Budget line item deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteBudgetLineItemRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.deleteBudgetLineItem, state.loadingKeys);
                toast.error(
                    `Budget line item can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(upsertBudgetLineItemFormulaRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.updateFormulaByBudgetLineItem];
            })
            .addCase(upsertBudgetLineItemFormulaRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateFormulaByBudgetLineItem, state.loadingKeys);
                toast.success(`Formula is updated.`, { position: "bottom-right" })
            })
            .addCase(upsertBudgetLineItemFormulaRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateFormulaByBudgetLineItem, state.loadingKeys);
                toast.error(
                    `Formula can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updateMonthValueRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLineItemLoadingKeys.updateMonthValue];
            })
            .addCase(updateMonthValueRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateMonthValue, state.loadingKeys);
            })
            .addCase(updateMonthValueRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(BudgetLineItemLoadingKeys.updateMonthValue, state.loadingKeys);
                toast.error(
                    `Failed to update month value. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
    }
})
export const { clearBudgetLineItems, clearBudgetLineItemsBySourceAccount } = budgetLineItemSlice.actions;
export default budgetLineItemSlice.reducer 